import { callSocketUrl } from "./protocol";

/** Refresh only when the access token expires within this many seconds. */
export const REFRESH_WITHIN_S = 60;
/** The refresh request may take at most this long; then the stored token is used. */
export const REFRESH_TIMEOUT_MS = 3_000;

export interface FreshSocketUrlDeps {
  apiBaseUrl: string | undefined;
  callId: number;
  /** Any authenticated request: a 401 on it runs the portal's refresh flow
   *  (lib/api/client.ts), which stores the new access token. Errors are ignored. */
  refresh: (signal: AbortSignal) => Promise<unknown>;
  readToken: () => string | null;
  /** Seconds since the epoch (tests). */
  nowSeconds?: () => number;
}

/** A JWT's `exp` (seconds), or null when the token can't be parsed. */
export function jwtExpiry(token: string): number | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === "number" ? exp : null;
  } catch {
    return null;
  }
}

/** The call socket's URL with an access token that is current right now. The
 *  access token lasts 15 minutes and the call page makes no HTTP requests while
 *  connected, so a stored token may have expired by the time a dropped socket
 *  reconnects. Only when it expires within a minute (or can't be read) touch the
 *  API so the refresh interceptor can renew it, and never wait longer than 3 s:
 *  the 15 s grace is running. */
export async function freshCallSocketUrl(deps: FreshSocketUrlDeps): Promise<string | null> {
  const now = deps.nowSeconds ?? (() => Date.now() / 1000);
  const stored = deps.readToken();
  const exp = stored ? jwtExpiry(stored) : null;
  if (stored && (exp === null || exp - now() <= REFRESH_WITHIN_S)) {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        deps.refresh(controller.signal),
        new Promise<void>((resolve) => {
          timer = setTimeout(() => {
            controller.abort();
            resolve();
          }, REFRESH_TIMEOUT_MS);
        }),
      ]);
    } catch {
      // Offline or a transient error: try the stored token anyway.
    } finally {
      clearTimeout(timer);
    }
  }
  const token = deps.readToken();
  return token && deps.apiBaseUrl ? callSocketUrl(deps.apiBaseUrl, deps.callId, token) : null;
}
